/**
 * Wspólny fetch dla outbox — ten sam CSRF i 401 co api/client.
 */

import { getCsrfToken } from "../../api/csrf";
import { isPublicAuthPath } from "../publicAuthPath";
import { clearSoftSession } from "./offlineAuth";

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

export type OfflineFetchResult = {
  ok: boolean;
  status: number;
  data: unknown;
  error: unknown;
};

function handleUnauthorized(path: string): void {
  if (path.startsWith("/api/auth/")) {
    return;
  }
  if (isPublicAuthPath(window.location.pathname)) {
    return;
  }
  clearSoftSession();
  window.location.replace("/app/login");
}

async function parseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }
  const text = await response.text();
  return text || null;
}

export async function offlineFetch(
  method: string,
  path: string,
  body?: Record<string, unknown>,
): Promise<OfflineFetchResult> {
  const headers: Record<string, string> = {};
  if (!SAFE_METHODS.has(method.toUpperCase())) {
    headers["Content-Type"] = "application/json";
    const token = getCsrfToken();
    if (token) {
      headers["X-CSRF-Token"] = token;
    }
  }

  const response = await fetch(path, {
    method,
    headers,
    credentials: "same-origin",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401) {
    handleUnauthorized(path);
  }

  const parsed = await parseBody(response);
  if (response.ok) {
    return { ok: true, status: response.status, data: parsed, error: undefined };
  }
  return {
    ok: false,
    status: response.status,
    data: undefined,
    error: parsed ?? { detail: `HTTP ${response.status}` },
  };
}
